import React, { useCallback, useContext, useMemo } from 'react';
import { DocumentCarouselContext, IActionCriteria, IDocumentPrint } from '../../components/document/DocumentProvider';
import { useQuery } from '@apollo/client';
import {
  IStockTakeLine,
  IStockTakeLineDocumentCarouselQueryResult,
  IStockTakeLineDocumentCarouselQueryVariables,
  IStockTakeReportMutationResult,
  IStockTakeReportMutationVariables,
  Maybe,
  ReportType,
} from '../../graphql/schema/p4types';
import {
  getDocumentCarouselReportParameters,
  IDocumentValues,
  useActionFilters,
  useDocumentCarouselDefaults,
} from '../../components/document/documentCarouselUtil';
import DocumentCarouselHeader from '../../components/document/DocumentCarouselHeader';
import { FormProvider, useForm } from 'react-hook-form';
import DocumentCarouselTemplate from '../../components/document/DocumentCarouselTemplate';
import DocumentCarouselOutput from '../../components/document/DocumentCarouselOutput';
import DocumentCarouselButtons from '../../components/document/DocumentCarouselButtons';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import { useDocumentCarouselComplete, useDocumentJobCompletion } from '../../utils/DocumentUtils';
import getClientMutationId from '../../utils/getClientMutationId';
import DocumentCarouselEmail from '../../components/document/DocumentCarouselEmail';
import StockTakeLineDocumentCarouselQuery from '../../graphql/queries/document/StockTakeLineDocumentCarouselQuery.graphql';
import DocumentCarouselPrintOptions from '../../components/document/DocumentCarouselPrintOptions';
import DocumentCarouselSummary from '../../components/document/DocumentCarouselSummary';
import { useCookies } from 'react-cookie';
import { DocumentCarouselHeaderInfo } from '../../components/document/DocumentCarouselHeaderInfo';
import DocumentCarouselSkeleton from '../../components/document/DocumentCarouselSkeleton';
import StockTakeReportMutation from '../../graphql/mutations/product/StockTakeReportMutation.graphql';
import * as entities from '../../graphql/entities';
import { useSelector } from 'react-redux';
import { IReduxStore } from '../../@types/reduxTypes';

//Note - DocumentModal2 currently uses 'documentSummary' instead of 'DOCUMENT_SUMMARY' - Possible cause for minor friction in transition.
export const DOCUMENT_SUMMARY = 'DOCUMENT_SUMMARY';
export const PRINT_INDIVIDUAL_PREFERENCE_COOKIE = 'PRINT_INDIVIDUAL_PREFERENCE_COOKIE';

/**
 * Render the content for the Stock Take Line Document Carousel.
 * All querying should be completed before rendering this element so that 'documentDefaults' are accurate/up-to-date when the user see's them.
 * @param documentDefaults - Default document values. Should be final on initial load.
 * @param actionCriteria - The action criteria driving this particular print.
 * @param count - How many records are in this print job.
 * @param singleRecipient - Flag to indicate if all records are a single Customer/Supplier.
 * @param singlePrint - Flag to indicate if we're printing a single record.
 * @param singlePrintNode - If we're printing a single record, this should be the document-queried subset of that record for display/defaulting.
 *                        - Note - Will still be populated for non-single, will be arbitrarily the first record matching the criteria.
 * @param setCookie - Pass-through of cookie setter, used to update 'showSummary' and 'printContiguous' cookie values.
 * @param documentPrint - Print action object - Provides additional detail such as whether this is a reprint, print completion callback or additional email configuration.
 */
const StockTakeDocumentContent = ({
  documentDefaults,
  actionCriteria,
  count,
  singleRecipient,
  singlePrint,
  singlePrintNode,
  setCookie,
  documentPrint,
  entity,
}: {
  documentDefaults: IDocumentValues;
  actionCriteria: IActionCriteria;
  count: number;
  singleRecipient: boolean;
  singlePrint: boolean;
  singlePrintNode: IStockTakeLine;
  setCookie: ReturnType<typeof useCookies>[1];
  documentPrint: IDocumentPrint;
  entity: EntityType;
}) => {
  const { onSkip } = useContext(DocumentCarouselContext);
  const { reprint, onPrintComplete } = documentPrint;

  //Use react-hook-form to manage our form values for better typing and performance than ConnectedForm.
  const methods = useForm<IDocumentValues>({
    defaultValues: documentDefaults,
  });
  const { getValues, watch } = methods;

  const onDocumentComplete = useDocumentCarouselComplete({
    watch,
    entity,
  });

  const onJobStart = useCallback(() => {
    onSkip(true);
  }, [onSkip]);

  const onJobComplete = useDocumentJobCompletion({
    evictionFieldNames: [],
    onDocumentComplete,
    onPrintComplete,
  });

  const [runStockTakeReport] = useBackgroundMutation<IStockTakeReportMutationResult, IStockTakeReportMutationVariables>(
    StockTakeReportMutation,
    {
      onStartJob: onJobStart,
      onCompleteJob: onJobComplete,
    }
  );

  const onPrintClick = useCallback(() => {
    const reportParameters = getDocumentCarouselReportParameters({ getValues, reprint });

    runStockTakeReport({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          actionCriteria,
          reportParameters: reportParameters,
        },
      },
    });
  }, [actionCriteria, runStockTakeReport]);

  return (
    <FormProvider {...methods}>
      <DocumentCarouselHeader
        entity={entity}
        count={count}
        secondaryTitle={
          <DocumentCarouselHeaderInfo
            contentLeft={singlePrint ? `Product ${singlePrintNode?.product?.reference}` : undefined}
            contentRight={
              singlePrint ? `Bin Location ${singlePrintNode?.binLocation?.location ?? 'Unknown'}` : undefined
            }
          />
        }
        reprint={reprint}
      />
      <div style={{ padding: '1em' }}>
        <DocumentCarouselTemplate reportType={ReportType.StockTakeReport} singleRecipient={singleRecipient} />
        <DocumentCarouselOutput singleRecipient={singleRecipient} />
        <DocumentCarouselSummary singlePrint={singlePrint} setCookie={setCookie} />
        <DocumentCarouselPrintOptions singlePrint={singlePrint} setCookie={setCookie} />
        <DocumentCarouselEmail
          entity={entity}
          singleRecipient={singleRecipient}
          emailOptions={{
            additionalRecipients: [],
            allowEmailTo: true,
            allowEmailCc: true,
            allowSubject: true,
          }}
        />
      </div>
      <DocumentCarouselButtons onPrint={onPrintClick} />
    </FormProvider>
  );
};

/**
 * @param actionCriteria - The action criteria driving this particular print.
 * @param reprint - This is a reprint operation, will will use a different mutation and data queries.
 */
export const StockTakeDocumentCarouselCard = ({
  actionCriteria,
  documentPrint,
}: {
  actionCriteria: IActionCriteria;
  documentPrint: IDocumentPrint;
}) => {
  const filters = useActionFilters(actionCriteria);
  const { data, loading } = useQuery<
    IStockTakeLineDocumentCarouselQueryResult,
    IStockTakeLineDocumentCarouselQueryVariables
  >(StockTakeLineDocumentCarouselQuery, {
    variables: {
      filters: filters,
      first: 2,
      aggregateCriteria: {
        groupColumns: ['product.id'],
        sumColumns: ['id'],
      },
    },
  });

  const singlePrint: boolean = actionCriteria.include && actionCriteria.actionEntities.length === 1;
  //If the grouping gives a single Product in the result, we can use it to indicate the product reference in the carousel header.
  //(If there's >1 Customer, we cannot do this).
  const singleRecipient = data?.listData?.aggregateRows?.length === 1;
  const singlePrintNode = data?.listData?.edges?.[0]?.node as Maybe<IStockTakeLine>;

  const emailAddress = useSelector((store: IReduxStore) => store.app?.user?.email);
  const stockTakeLineReportEntity = useMemo(() => {
    return {
      ...entities.StockTakeLine,
      configuration: {
        title: 'Stock Take Report',
        reportType: ReportType.StockTakeReport,
        defaultConfig: {
          emailList: [{ id: emailAddress, email: emailAddress }],
        },
      },
    };
  }, [emailAddress]);

  const [cookies, setCookie] = useCookies([DOCUMENT_SUMMARY, PRINT_INDIVIDUAL_PREFERENCE_COOKIE]);
  const documentDefaults = useDocumentCarouselDefaults({
    entity: stockTakeLineReportEntity,
    commonDocumentDefault: {}, // There is no defaults for Stock Take printing.
    singleRecipient: true,
    cookies,
    allowEntityEmail: false,
  });

  if (data && !loading) {
    return (
      <StockTakeDocumentContent
        documentDefaults={documentDefaults}
        setCookie={setCookie}
        actionCriteria={actionCriteria}
        count={data?.listData?.count ?? 0}
        singleRecipient={true}
        singlePrint={singlePrint}
        singlePrintNode={singlePrintNode}
        documentPrint={documentPrint}
        entity={stockTakeLineReportEntity}
      />
    );
  }

  return <DocumentCarouselSkeleton />;
};
